import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TireQuoteRecordCustomer } from '@navio/nsdk';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiBadge, PrettySlug } from 'noddi-ui-web';
import { formatCurrencyAmount, formatLicensePlate, getCarDisplayName } from 'noddi-util';
import { CompactTireSelectionCard, NavioTireSeasonChip, type CompactTireSelectionCardTranslations } from 'noddi-web';

import { getCommonTranslations } from '../../../../utils/commonTranslations';
import { getGenericItemsTotal, getOfferTotalForOption, sortQuoteItemsByTier } from '../utils/applyStrategy';

type OfferRowProps = {
  offer: TireQuoteRecordCustomer;
  selectedQuoteItemId: number | undefined;
  onSelectQuoteItem: (quoteItemId: number) => void;
};

export const OfferRow = ({ offer, selectedQuoteItemId, onSelectQuoteItem }: OfferRowProps) => {
  const isSelected = selectedQuoteItemId != null;
  const visibleItems = sortQuoteItemsByTier(offer.quoteItems).slice(0, 3);
  const selectedItem = offer.quoteItems.find((item) => item.id === selectedQuoteItemId);

  const selectedTotal = selectedItem ? getOfferTotalForOption(offer, selectedItem) : 0;
  const genericItemsTotal = getGenericItemsTotal(offer);
  const tireCount = selectedItem
    ? selectedItem.quoteItemTires.reduce((sum, tire) => sum + (tire.quantityDefault ?? 0), 0)
    : 0;
  const currency = selectedItem?.quoteItemTires[0]?.tireInventory.salesItem.price.priceGross.currency;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 border-b border-border bg-card p-4',
        'md:grid md:grid-cols-[28px_minmax(180px,220px)_1fr_minmax(110px,140px)] md:items-center md:gap-4'
      )}
    >
      <div className='flex items-center gap-3 md:contents'>
        <span
          className={cn(
            'flex size-[18px] shrink-0 items-center justify-center rounded-md border-2 transition-colors',
            isSelected
              ? 'border-card-selected-background bg-card-selected-background text-card-selected-foreground'
              : 'border-border bg-card'
          )}
        >
          {isSelected && <NoddiIcon name='Check' size='xSmall' />}
        </span>

        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='truncate text-sm font-semibold text-foreground'>
              {getCarDisplayName({ car: offer.car, showLicensePlate: false, showYear: false })}
            </span>
            {offer.car.licensePlateNumber && (
              <NoddiBadge variant='outline' className='font-mono text-[11px] font-semibold tracking-wide'>
                {formatLicensePlate(offer.car.licensePlateNumber)}
              </NoddiBadge>
            )}
          </div>
          <div className='truncate text-[11px] text-muted-foreground'>
            #<PrettySlug slug={offer.slug} />
          </div>
        </div>
      </div>

      <div className='grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3'>
        {visibleItems.map((item) => (
          <CompactTireSelectionCard
            key={item.id}
            quoteItem={item}
            additionalAmount={genericItemsTotal}
            isSelected={selectedQuoteItemId === item.id}
            onSelect={() => onSelectQuoteItem(item.id)}
            translations={getCompactTireSelectionCardTranslations()}
          />
        ))}
      </div>

      {selectedItem ? (
        <div
          className={cn(
            'flex items-center justify-between gap-3 rounded-lg bg-card-light-background px-3 py-2',
            'md:flex-col md:items-end md:justify-start md:gap-1 md:rounded-none md:bg-transparent md:p-0 md:text-right'
          )}
        >
          <div className='flex items-center gap-2 md:order-1 md:flex-col md:items-end md:gap-1'>
            <NavioTireSeasonChip
              season={offer.season.value}
              size='sm'
              translations={getCommonTranslations().seasonBadge}
            />
            <span className='text-[10px] font-semibold tracking-wider text-muted-foreground uppercase'>
              <Trans>Selected · {tireCount} pcs</Trans>
            </span>
          </div>
          <div className='text-base font-bold text-primary tabular-nums md:text-foreground'>
            {formatCurrencyAmount({ money: { amount: selectedTotal, currency }, decimals: 0 })}
          </div>
        </div>
      ) : (
        <div className='hidden text-[11px] text-muted-foreground italic md:block md:text-right'>
          <Trans>
            No option
            <br />
            selected yet
          </Trans>
        </div>
      )}
    </div>
  );
};

function getCompactTireSelectionCardTranslations(): CompactTireSelectionCardTranslations {
  return {
    recommended: t`Recommended`,
    budget: t`Budget`,
    standard: t`Standard`,
    premium: t`Premium`
  };
}
